import { rockAhead, routeTargetX, steerToward, type Obstacle, type Route } from '../game/Pilot';
import { RIVER_LENGTH, type River } from '../game/River';
import { FLAP_COOLDOWN, PADDLE_CHARGES, type Tuning } from '../game/tuning';
import { Duck } from './Duck';
import type { Item } from './PowerUps';

export type RivalProfile = {
  name: string;
  body: string;
  beak: string;
  /** How far ahead (m) it reads the water. Further = smoother lines. */
  look: number;
  /** How far off its line (m) it tolerates before correcting. */
  deadband: number;
  /** Seconds between steering decisions (reaction time). */
  reaction: number;
  /** Only paddles when the water is at least this fast (0..1 of the fastest). */
  surf: number;
  /** Chance per second of a clumsy wobble. */
  wobble: number;
  /** Which way it goes at the split. */
  route: Route;
  /** Sits this far (m) to the side of the line it follows. */
  bias: number;
  /** Fraction of a full nudge it steers with (weaker = drifts off the line more). */
  strength: number;
  /**
   * Seconds per paddle charge. Rivals don't collect crumbs, so this steady
   * refill stands in for what they'd earn: lower = more paddles.
   */
  recharge: number;
  /** How keenly it flaps out of slow water (1 = every flap cooldown). */
  flapRate: number;
  /** Seconds ahead it spots rocks to steer around (at its current speed). */
  avoid: number;
  /** Holds its paddle while a rock is this many metres ahead (0 = never). */
  care: number;
};

export const RIVAL_PROFILES: readonly RivalProfile[] = [
  // The ace: reads far ahead, spots rocks and logs early, paddles often, takes the shortcut. Gold pace.
  { name: 'Bill', body: '#4aa3ff', beak: '#ff8a1c', look: 3.4, deadband: 0.15, reaction: 0.05, surf: 0.68, wobble: 0, route: 'shortcut', bias: 0, strength: 1, recharge: 1.4, flapRate: 1, avoid: 1.1, care: 7 },
  // Sharp but careful: plays it safe at the split, otherwise nearly as clean as Bill. Silver pace.
  { name: 'Rosie', body: '#ff85bd', beak: '#ff8a1c', look: 3, deadband: 0.22, reaction: 0.08, surf: 0.72, wobble: 0.02, route: 'safe', bias: 0.3, strength: 1, recharge: 1.9, flapRate: 0.8, avoid: 1, care: 6 },
  // Clumsy: late reactions, wobbles, rarely paddles, only sees rocks at the last moment.
  { name: 'Sprout', body: '#7ed957', beak: '#ffb020', look: 1.2, deadband: 0.5, reaction: 0.25, surf: 0.97, wobble: 0.3, route: 'shortcut', bias: -0.35, strength: 0.55, recharge: 4.5, flapRate: 0.5, avoid: 0, care: 0 },
];

/** How far (m) from the player before a rival eases off or pushes harder. */
const RUBBER_BAND = 12;

/** An AI duck: the same physics as the player, driven by a simple pilot. */
export class Rival {
  readonly duck: Duck;
  finishTime: number | null = null;
  private charges = PADDLE_CHARGES;
  private steer = 0;
  private decideTimer = 0;
  private wobbleTimer = 0;
  private wobbleDir = 0;
  private holdTime = 0;
  private flapTimer = 0;

  constructor(
    readonly profile: RivalProfile,
    private readonly rng: () => number,
  ) {
    this.duck = new Duck({ body: profile.body, beak: profile.beak });
  }

  reset(x: number, y: number, z: number): void {
    this.duck.reset(x, y, z);
    this.finishTime = null;
    this.charges = PADDLE_CHARGES;
    this.steer = 0;
    this.decideTimer = 0;
    this.wobbleTimer = 0;
    this.holdTime = 0;
    this.flapTimer = 0;
  }

  /** Whether to use the held item now. */
  wantsToUse(item: Item, player: Duck, river: River, delta: number): boolean {
    this.holdTime += delta;
    if (this.finishTime !== null) return false;
    if (item === 'shield') return this.holdTime > 0.4;
    if (item === 'lure') {
      const gap = Math.hypot(player.position.x - this.duck.position.x, player.position.z - this.duck.position.z);
      return (gap < 10 && this.holdTime > 0.6) || this.holdTime > 8;
    }
    // Boost only with clear water ahead.
    if (this.holdTime < 0.8) return false;
    const d = this.duck.distance;
    return !river.rocks.some((rock) => rock.d - d > 0 && rock.d - d < 7 && Math.abs(rock.x - this.duck.position.x) < 1.6);
  }

  /** Call after the item was used. */
  usedItem(): void {
    this.holdTime = 0;
  }

  get distance(): number {
    return this.duck.distance;
  }

  /**
   * `racing` false holds it at the line (countdown) or lets it drift home
   * after the finish. `playerD` drives the gentle rubber band.
   */
  update(
    delta: number,
    live: boolean,
    racing: boolean,
    raceTime: number,
    playerD: number,
    river: River,
    tuning: Tuning,
    lure: { x: number; z: number } | null = null,
    obstacles: readonly Obstacle[] = [],
  ): void {
    const p = this.profile;
    const steering = racing && this.finishTime === null;
    if (steering) {
      this.charges = Math.min(PADDLE_CHARGES, this.charges + delta / p.recharge);

      this.decideTimer -= delta;
      if (this.decideTimer <= 0) {
        this.decideTimer = p.reaction * (0.7 + this.rng() * 0.6);
        // A bread lure in range is irresistible.
        const target = lure ? lure.x : routeTargetX(this.duck, river, p.route, p.look) + p.bias;
        const speed = Math.hypot(this.duck.velocity.x, this.duck.velocity.z);
        // Only sharp-eyed rivals (avoid > 0) watch the logs too, and plan their side of each rock.
        this.steer = steerToward(this.duck, river, target, p.deadband, Math.max(4, speed * p.avoid), p.avoid > 0 ? obstacles : [], p.avoid > 0);
        if (this.wobbleTimer <= 0 && this.rng() < p.wobble * p.reaction) {
          this.wobbleTimer = 0.3 + this.rng() * 0.3;
          this.wobbleDir = this.rng() < 0.5 ? -1 : 1;
        }
      }
      this.wobbleTimer -= delta;

      // Paddle in fast water; push harder when behind the player, ease off when well ahead.
      const gap = playerD - this.duck.distance;
      const threshold = p.surf + (gap < -RUBBER_BAND ? 0.12 : gap > RUBBER_BAND ? -0.25 : 0);
      const water01 = this.duck.flow.speed / river.maxSpeed();
      const careful = p.care > 0 && rockAhead(this.duck, river, p.care);
      if (this.charges >= 1 && !this.duck.stunned && !careful && water01 > threshold && this.rng() < delta * 3) {
        this.charges -= 1;
        const surf = Math.max(0, Math.min(1, (water01 - 0.45) / 0.5));
        this.duck.paddle(tuning.paddleKick + tuning.paddleFastBonus * surf, river, tuning);
      }
      // Flap out of stalls, at a rival's own keenness.
      this.flapTimer -= delta;
      if (this.flapTimer <= 0 && this.duck.canFlap(river, tuning)) {
        this.flapTimer = FLAP_COOLDOWN / p.flapRate;
        this.duck.flap(river, tuning);
      }
    }

    const steer = steering ? (this.wobbleTimer > 0 ? this.wobbleDir : this.steer) * p.strength : 0;
    this.duck.update(delta, steer, live, river, tuning);
    if (this.finishTime === null && racing && this.duck.distance >= RIVER_LENGTH) this.finishTime = raceTime;
  }

  dispose(): void {
    this.duck.dispose();
  }
}
